import React, { useEffect, useState } from 'react';
import { Coins, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import { creditApi, CreditBalance, WarningLevel } from '../services/creditApi';
import { useAuth } from '../contexts/AuthContext';

interface CreditBalanceWidgetProps {
  compact?: boolean;
}

const WARNING_COLOURS: Record<WarningLevel, string> = {
  NONE: 'text-emerald-600 dark:text-emerald-400',
  LOW: 'text-amber-600 dark:text-amber-400',
  CRITICAL: 'text-red-600 dark:text-red-400',
  DEPLETED: 'text-red-700 dark:text-red-300',
};

export const CreditBalanceWidget: React.FC<CreditBalanceWidgetProps> = ({ compact = false }) => {
  const [balance, setBalance] = useState<CreditBalance | null>(null);
  const [error, setError] = useState(false);
  const { token } = useAuth();

  useEffect(() => {
    if (!token) return;

    const load = async () => {
      try {
        const data = await creditApi.getBalance();
        setBalance(data);
        setError(false);
      } catch (err) {
        console.error('[CreditBalanceWidget] Failed to load balance:', err);
        setError(true);
      }
    };

    load();

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') load();
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, [token]);

  if (error) {
    if (compact) {
      return (
        <Link
          to="/account"
          className="flex items-center gap-1.5 text-medical-400 dark:text-medical-500 hover:opacity-80 transition-opacity"
          title="Unable to load page balance"
        >
          <AlertCircle className="h-3.5 w-3.5" />
          <span className="text-xs">--</span>
        </Link>
      );
    }
    return (
      <Link to="/account" className="block hover:opacity-90 transition-opacity">
        <div className="flex items-center gap-1">
          <AlertCircle className="h-3 w-3 text-medical-400 dark:text-medical-500" />
          <span className="text-xs text-medical-400 dark:text-medical-500">
            Balance unavailable
          </span>
        </div>
      </Link>
    );
  }

  if (!balance) return null;

  const warningLevel = balance.warningLevel || 'NONE';

  // For subscribers: show monthly consumption cost
  // For non-subscribers: show free page count
  if (compact) {
    if (balance.hasActiveSubscription) {
      const costDisplay = balance.monthlyConsumptionCostPence > 0
        ? `£${(balance.monthlyConsumptionCostPence / 100).toFixed(2)}`
        : '£0.00';
      return (
        <Link
          to="/account"
          className="flex items-center gap-1.5 text-emerald-600 dark:text-emerald-400 hover:opacity-80 transition-opacity"
          title={`${balance.monthlyConsumptionPages} pages this month (${costDisplay})${balance.freePageBalance > 0 ? ` · ${balance.freePageBalance} free pages` : ''}`}
        >
          <Coins className="h-3.5 w-3.5" />
          <span className="text-xs font-semibold">
            {balance.freePageBalance > 0 ? `${balance.freePageBalance} free` : costDisplay}
          </span>
        </Link>
      );
    }

    return (
      <Link
        to="/account"
        className={`flex items-center gap-1.5 ${WARNING_COLOURS[warningLevel]} hover:opacity-80 transition-opacity`}
        title={`${balance.freePageBalance} free pages remaining`}
      >
        <Coins className="h-3.5 w-3.5" />
        <span className="text-xs font-semibold">{balance.freePageBalance}</span>
      </Link>
    );
  }

  // Full (non-compact) display
  if (balance.hasActiveSubscription) {
    const costDisplay = `£${(balance.monthlyConsumptionCostPence / 100).toFixed(2)}`;
    return (
      <Link to="/account" className="block hover:opacity-90 transition-opacity">
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-1">
            <Coins className="h-3 w-3 text-emerald-600 dark:text-emerald-400" />
            <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">
              {balance.monthlyConsumptionPages} pages
            </span>
          </div>
          <span className="text-[10px] text-medical-400 dark:text-medical-500">
            {costDisplay} this month
          </span>
        </div>
        {balance.freePageBalance > 0 && (
          <p className="text-[10px] text-medical-500 dark:text-medical-400">
            {balance.freePageBalance} free pages remaining
          </p>
        )}
      </Link>
    );
  }

  // Non-subscriber view
  return (
    <Link to="/account" className="block hover:opacity-90 transition-opacity">
      <div className="flex items-center gap-1">
        <Coins className={`h-3 w-3 ${WARNING_COLOURS[warningLevel]}`} />
        <span className={`text-xs font-semibold ${WARNING_COLOURS[warningLevel]}`}>
          {balance.freePageBalance} pages
        </span>
      </div>
      {warningLevel === 'DEPLETED' && (
        <p className="text-[10px] text-red-600 dark:text-red-400 mt-0.5 font-medium">
          Out of pages
        </p>
      )}
    </Link>
  );
};
